import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { parseAAMVA, licenseGuards, LIC_FIELD_KEYS } from "@/lib/aamva";
import { readPdf417 } from "@/lib/barcode";
import { toast } from "sonner";
import { Camera, RefreshCw, CheckCircle2, ScanLine } from "lucide-react";

const EMPTY_FIELDS = LIC_FIELD_KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {});

function saveErrorMessage(detail) {
  if (detail && typeof detail === "object" && typeof detail.reason === "string") return detail.reason;
  return formatApiError(detail);
}

// Both sides at once, read silently (back barcode first, then front vision fallback), then a
// plain review-and-correct form. See ~/nemt-contracts/D_license.md for the full flow spec.
export default function LicenseCapture({ onDone }) {
  const { user } = useAuth();
  const driverId = user?.driver_id;

  const [step, setStep] = useState("capture"); // capture | review | success
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [emptyRead, setEmptyRead] = useState(false);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [source, setSource] = useState("manual");
  const [saveError, setSaveError] = useState(null);
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);

  const pickFront = (file) => {
    setFront(file || null);
    setFrontPreview(file ? URL.createObjectURL(file) : null);
    setEmptyRead(false);
  };
  const pickBack = (file) => {
    setBack(file || null);
    setBackPreview(file ? URL.createObjectURL(file) : null);
  };

  const uploadSide = async (file, side) => {
    const fd = new FormData();
    fd.append("doc_type", "drivers_license");
    fd.append("side", side);
    fd.append("file", file);
    const { data } = await api.post(`/drivers/${driverId}/documents`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.id;
  };

  const applyFields = (f, src) => {
    setFields({ ...EMPTY_FIELDS, ...f });
    setSource(src);
  };

  const next = async () => {
    if (!front || !back) {
      toast.error("Please add a photo of both the front and back of your license.");
      return;
    }
    setBusy(true);
    setStatusMsg("Uploading your license…");
    try {
      const frontDocId = await uploadSide(front, "front");
      await uploadSide(back, "back");

      // Read silently: the back barcode first (free, no network), then the front vision
      // fallback. Either failing just means the driver fills the review form by hand.
      setStatusMsg("Reading your license…");
      let readFields = null;
      let readSource = null;
      try {
        const raw = await readPdf417(back);
        if (raw) {
          const parsed = parseAAMVA(raw);
          if (parsed) {
            readFields = parsed;
            readSource = "barcode";
          }
        }
      } catch (e) {
        // fall through to the vision fallback
      }

      if (!readFields) {
        try {
          const { data } = await api.post(`/drivers/${driverId}/license/extract`, { doc_id: frontDocId });
          if (data && data.fields) {
            readFields = data.fields;
            readSource = data.source || "vision";
          }
        } catch (e) {
          // 503 (no vision key configured) or any other failure: fall through to manual entry
        }
      }

      if (readFields) {
        applyFields(readFields, readSource);
        setEmptyRead(false);
      } else {
        applyFields(EMPTY_FIELDS, "manual");
        setEmptyRead(true);
      }
      setStep("review");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed, check your connection and try again.");
    } finally {
      setBusy(false);
      setStatusMsg("");
    }
  };

  const setField = (key, value) => setFields((f) => ({ ...f, [key]: value }));

  const guards = licenseGuards(fields);
  const guardFor = (key) => guards.find((g) => g.field === key)?.message;

  const save = async () => {
    setSaveError(null);
    setBusy(true);
    try {
      await api.put(`/drivers/${driverId}/license-prefill`, { ...fields, source, confirmed: true });
      setStep("success");
    } catch (e) {
      setSaveError(saveErrorMessage(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const retryFront = () => {
    setFront(null);
    setFrontPreview(null);
    setEmptyRead(false);
    setStep("capture");
    setTimeout(() => frontInputRef.current?.click(), 0);
  };

  if (step === "success") {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-6 text-center" data-testid="license-success">
        <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={32} />
        <div className="font-display font-semibold text-slate-900 mb-1">License details saved</div>
        <p className="text-sm text-slate-600 mb-5">Your manager will review it along with the rest of your file.</p>
        <button onClick={onDone} className="btn-primary text-sm px-4 py-2.5" data-testid="license-done">
          Back to driver portal
        </button>
      </div>
    );
  }

  if (step === "review") {
    return (
      <div className="space-y-4" data-testid="license-review">
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
          Please check every field below against your physical license and correct anything that is wrong before you save.
        </div>
        {emptyRead && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-600 flex items-center justify-between gap-2 flex-wrap">
            <span>We could not read this license automatically. Please type the details.</span>
            <button
              onClick={retryFront}
              className="text-[#005E82] hover:underline inline-flex items-center gap-1 whitespace-nowrap"
              data-testid="reupload-front"
            >
              <RefreshCw size={12} /> Re-upload a clearer front
            </button>
          </div>
        )}
        <ReviewForm fields={fields} setField={setField} guardFor={guardFor} />
        {saveError && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="save-error">
            {saveError}
          </div>
        )}
        <button
          onClick={save}
          disabled={busy}
          className="w-full btn-primary text-sm py-3 disabled:opacity-60"
          data-testid="save-license"
        >
          {busy ? "Saving…" : "Save license details"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="license-capture">
      <Tile
        label="Front of license"
        file={front}
        preview={frontPreview}
        inputRef={frontInputRef}
        onPick={pickFront}
        testId="front"
        hint={
          front && !back ? (
            <button
              type="button"
              onClick={() => backInputRef.current?.click()}
              className="text-[#005E82] hover:underline text-xs inline-flex items-center gap-1 mt-2"
              data-testid="scan-back-instead"
            >
              <ScanLine size={12} /> Scan the back instead
            </button>
          ) : null
        }
      />
      <Tile label="Back of license" file={back} preview={backPreview} inputRef={backInputRef} onPick={pickBack} testId="back" />
      <div className="text-xs text-slate-500">We keep both sides on file for your records.</div>
      {statusMsg && (
        <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="license-status">
          <span className="animate-pulse">●</span>
          {statusMsg}
        </div>
      )}
      <button
        onClick={next}
        disabled={busy || !front || !back}
        className="w-full btn-primary text-sm py-3 disabled:opacity-60"
        data-testid="license-next"
      >
        Next
      </button>
    </div>
  );
}

function Tile({ label, file, preview, inputRef, onPick, testId, hint }) {
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-slate-800">{label}</div>
        {file && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-[#005E82] hover:underline text-xs"
            data-testid={`replace-${testId}`}
          >
            Replace
          </button>
        )}
      </div>
      {preview ? (
        <img src={preview} alt={label} className="w-full max-h-48 object-contain rounded-md border border-slate-100" />
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full min-h-[44px] flex items-center justify-center gap-2 border border-dashed border-slate-300 rounded-md py-6 text-slate-500 text-sm"
          data-testid={`pick-${testId}`}
        >
          <Camera size={16} /> Add photo
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
        data-testid={`file-${testId}`}
      />
      {hint}
    </div>
  );
}

function ReviewForm({ fields, setField, guardFor }) {
  const row = (key, label, opts = {}) => (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</label>
      <input
        type={opts.type || "text"}
        value={fields[key] || ""}
        onChange={(e) => setField(key, e.target.value)}
        className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
        data-testid={`fld-${key}`}
      />
      {guardFor(key) && <div className="text-xs text-rose-600 mt-1">{guardFor(key)}</div>}
    </div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {row("legal_first", "First name")}
        {row("legal_middle", "Middle name")}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {row("legal_last", "Last name")}
        {row("dob", "Date of birth", { type: "date" })}
      </div>
      {row("addr_street", "Street address")}
      <div className="grid grid-cols-2 gap-3">
        {row("addr_city", "City")}
        {row("addr_state", "State")}
      </div>
      {row("addr_zip", "ZIP")}
      <div className="grid grid-cols-2 gap-3">
        {row("license_number", "License number")}
        {row("license_class", "Class")}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {row("license_state", "License state")}
        {row("license_expiry", "Expiration date", { type: "date" })}
      </div>
    </div>
  );
}
